import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  Calendar,
  CircleArrowLeft,
  Clock,
  Filter,
  Loader2,
} from 'lucide-react';
import { useState } from 'react';

import {
  type Draft,
  useGetDrafts,
} from '~entities/payment/api/hourly-tuition/get-drafts';

import { DeleteHourlyTuitionDraftDialog } from '~features/payment/ui/hourly-tuition/delete-hourly-tuition-draft-dialog';

import { InputDatePicker } from '~shared/components/input-date-picker';
import { Badge } from '~shared/shadcn/ui/badge';
import { useStudioDateFormatter } from '~shared/hooks/useStudioDateFormatter';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';

export const HourlyTuitionDraftsPage = () => {
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    sessionId: 'all',
    fromDate: '',
    toDate: '',
  });

  const [appliedFilters, setAppliedFilters] = useState({
    page: 1,
    limit: 20,
    sessionId: '',
    fromDate: '',
    toDate: '',
  });

  const {
    data: draftsData,
    isLoading,
    isFetching,
    error,
  } = useGetDrafts(appliedFilters);

  const handleFilterChange = (key: string, value: string | number) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleApplyFilters = () => {
    const filtersToApply = {
      ...filters,
      sessionId: filters.sessionId === 'all' ? '' : filters.sessionId,
    };
    setAppliedFilters(filtersToApply);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      page: 1,
      limit: 20,
      sessionId: 'all',
      fromDate: '',
      toDate: '',
    };
    setFilters(clearedFilters);
    setAppliedFilters({
      ...clearedFilters,
      sessionId: '', // API expects empty string for no filter
    });
  };

  const drafts = draftsData?.data ?? [];
  const availableSessions = draftsData?.filters.sessions ?? [];

  if (isLoading) {
    return (
      <div className='flex min-h-[400px] items-center justify-center'>
        <div className='text-center'>
          <div className='mx-auto mb-4 size-8 animate-spin rounded-full border-b-2 border-[#002D62]'></div>
          <p className='text-gray-600'>Loading drafts...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex min-h-[400px] items-center justify-center'>
        <div className='text-center'>
          <p className='mb-4 text-red-600'>Error loading drafts</p>
          <Button
            onClick={() => {
              window.location.reload();
            }}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className=''>
      <div className='mb-6 border-b pb-4'>
        <Link
          className='flex w-fit items-center gap-2'
          to='/payment/hourly-tuition'
        >
          <CircleArrowLeft className='size-6 text-custom-secondary-1' />
          <h2 className='text-2xl font-semibold text-custom-secondary-1'>
            Hourly Tuition Drafts
          </h2>
        </Link>
      </div>

      {/* Filters */}
      <Card className='mb-6'>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <Filter className='size-5' />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4'>
            <div>
              <label className='mb-2 block text-sm font-medium'>Session</label>
              <Select
                value={filters.sessionId}
                onValueChange={(value) => {
                  handleFilterChange('sessionId', value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder='All Sessions' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>All Sessions</SelectItem>
                  {availableSessions.map((session) => (
                    <SelectItem
                      key={session.sessionId}
                      value={session.sessionId}
                    >
                      {session.sessionName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className='min-w-0 md:col-span-2'>
              <label className='mb-2 block text-sm font-medium'>
                Date range
              </label>
              <div className='flex min-w-0 items-center gap-2'>
                <InputDatePicker
                  id='hourly-draft-from-date'
                  value={filters.fromDate}
                  onChange={(value) => {
                    handleFilterChange('fromDate', value);
                  }}
                  ariaLabel='From date'
                  align='start'
                  displayFormat='MM/dd/yyyy'
                  className='h-8 min-w-0 flex-1 px-2'
                />
                <span className='shrink-0 text-sm text-muted-foreground'>
                  to
                </span>
                <InputDatePicker
                  id='hourly-draft-to-date'
                  value={filters.toDate}
                  onChange={(value) => {
                    handleFilterChange('toDate', value);
                  }}
                  ariaLabel='To date'
                  align='end'
                  displayFormat='MM/dd/yyyy'
                  className='h-8 min-w-0 flex-1 px-2'
                />
              </div>
            </div>

            <div className='flex items-end gap-2'>
              <Button onClick={handleApplyFilters} className='flex-1'>
                Filter
              </Button>
              <Button
                onClick={handleClearFilters}
                variant='outline'
                className='flex-1'
              >
                Clear
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Drafts List */}
      <div className='space-y-4'>
        {isFetching && (
          <div className='flex h-48 items-center justify-center'>
            <Loader2 className='size-8 animate-spin' />
          </div>
        )}
        {!isFetching && drafts.length === 0 ? (
          <Card>
            <CardContent className='py-12 text-center'>
              <p className='text-lg text-muted-foreground'>No drafts found</p>
              <p className='mt-2 text-sm text-muted-foreground'>
                Try adjusting your filters or create a new draft
              </p>
            </CardContent>
          </Card>
        ) : (
          drafts.map((draft) => <DraftCard key={draft.draftId} draft={draft} />)
        )}
      </div>

      {/* Pagination */}
      {draftsData?.pagination && draftsData.pagination.pages > 1 && (
        <div className='mt-6 flex items-center justify-between gap-3'>
          <p className='min-w-0 text-sm text-gray-600'>
            Showing {(filters.page - 1) * filters.limit + 1} to{' '}
            {Math.min(
              filters.page * filters.limit,
              draftsData.pagination.total,
            )}{' '}
            of {draftsData.pagination.total} drafts
          </p>
          <div className='flex shrink-0 gap-2'>
            <Button
              variant='outline'
              onClick={() => {
                handleFilterChange('page', filters.page - 1);
              }}
              disabled={filters.page <= 1}
            >
              Previous
            </Button>
            <Button
              variant='outline'
              onClick={() => {
                handleFilterChange('page', filters.page + 1);
              }}
              disabled={filters.page >= draftsData.pagination.pages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const DraftCard = ({ draft }: { draft: Draft }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { formatInStudioTz } = useStudioDateFormatter();

  const formatDate = (dateString: string) => {
    return formatInStudioTz(dateString, 'MMM d, yyyy');
  };

  const formatDateTime = (dateString: string) => {
    return formatInStudioTz(dateString, 'MMM d, yyyy hh:mm a');
  };

  const handleContinueEditing = () => {
    // Store draft data in React Query cache
    queryClient.setQueryData(['draft-continue'], {
      sessionId: draft.sessionId,
      fromDate: new Date(draft.fromDate),
      toDate: new Date(draft.toDate),
      autoFetch: true,
    });

    // Navigate to review page
    void navigate({
      to: '/payment/hourly-tuition/review',
    });
  };

  return (
    <Card className='transition-shadow hover:shadow-md'>
      <CardContent className='p-6'>
        <div className='mb-4 flex items-start justify-between gap-3'>
          <div>
            <h3 className='text-lg font-semibold text-gray-900'>
              Draft #{draft.draftId.slice(-8)}
            </h3>
            <p className='text-sm capitalize text-gray-600'>
              Session: {draft.sessionName}
            </p>
            <p className='text-sm text-gray-500'>ID: {draft.sessionId}</p>
          </div>
          <div className='text-right'>
            <Badge variant='secondary'>Draft</Badge>
          </div>
        </div>

        <div className='mb-4 grid grid-cols-1 gap-4 md:grid-cols-2'>
          <div className='flex items-center gap-2'>
            <Calendar className='size-4 text-gray-400' />
            <div>
              <p className='text-sm text-gray-600'>Billing Period</p>
              <p className='font-medium'>
                {formatDate(draft.fromDate)} - {formatDate(draft.toDate)}
              </p>
            </div>
          </div>

          <div className='flex items-center gap-2'>
            <Clock className='size-4 text-gray-400' />
            <div>
              <p className='text-sm text-gray-600'>Created</p>
              <p className='font-medium'>{formatDateTime(draft.createdAt)}</p>
            </div>
          </div>
        </div>

        <div className='flex items-center justify-between'>
          <div className='text-sm text-gray-500'>Draft ID: {draft.draftId}</div>
          <div className='flex gap-2'>
            <DeleteHourlyTuitionDraftDialog
              draftId={draft.draftId}
              sessionName={draft.sessionName}
              fromDate={draft.fromDate}
              toDate={draft.toDate}
            />
            <Button size='sm' onClick={handleContinueEditing}>
              Continue Editing
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
